// airspaceExtrusion.ts — 3D-extruded airspace + TFR volumes via MapLibre native.
//
// Day-1 spike (2026-05-16): use MapLibre's `fill-extrusion` paint type to
// render the existing `classBCD` and `tfr` GeoJSON sources as 3D prisms.
// No three.js needed for this — `fill-extrusion-base` and `fill-extrusion-height`
// drive the bottom + top of each prism directly from feature properties.
//
// Triggered by the new `enable3d` UI flag. When pitch > 0 the prisms become
// visible; when the user is heads-down at pitch=0 they're nearly invisible.
//
// Removal: `removeAirspaceExtrusion(map)` strips every layer + source this
// module added in one call.

import type { Map as MapLibreMap, FilterSpecification } from 'maplibre-gl';

/** All MapLibre layer IDs this module adds. Keep in sync with addAirspaceExtrusion(). */
export const EXTRUSION_LAYER_IDS = [
  'classBCD-3d',
  'tfr-3d',
  'sua-3d',
] as const;

/**
 * Convert a height value from the airspace feature properties (feet MSL) into
 * meters. The fill-extrusion-height paint property is in MapLibre map units,
 * which for the default Mercator projection at moderate latitudes ≈ meters at
 * the equator. We multiply feet→meters for accuracy.
 */
const FT_TO_M = 0.3048;

/**
 * Default fallback altitudes when a TFR doesn't include floor/ceiling in its
 * properties (the current FAA TFR proxy doesn't expose altitude fields). These
 * are coarse — we'd rather show "a TFR exists here, vertical extent: surface
 * to 18,000 ft" than nothing.
 */
const TFR_DEFAULT_FLOOR_FT = 0;
const TFR_DEFAULT_CEILING_FT = 18_000;

/**
 * Add fill-extrusion layers ON TOP of the existing classBCD / tfr / sua sources.
 * Idempotent: if the layers already exist, no-op.
 *
 * The layers are added with `visibility: 'none'` — call `setExtrusionVisible()`
 * to flip them on. Visibility is decoupled from the 2D classBCD/tfr/sua fill
 * layers so the user can have 2D fills + 3D extrusions simultaneously (so the
 * boundaries are visible from straight-down view).
 */
export function addAirspaceExtrusion(map: MapLibreMap): void {
  // Class B/C/D — convert feet → meters via expression; clamp negative to 0
  if (!map.getLayer('classBCD-3d') && map.getSource('classBCD')) {
    map.addLayer({
      id: 'classBCD-3d',
      type: 'fill-extrusion',
      source: 'classBCD',
      layout: { visibility: 'none' },
      paint: {
        'fill-extrusion-color': [
          'match', ['get', 'CLASS'],
          'B', '#1d4ed8',
          'C', '#9d174d',
          'D', '#1e3a5f',
          /* default */ '#374151',
        ],
        'fill-extrusion-opacity': 0.30,
        'fill-extrusion-base': [
          '*',
          ['max', ['coalesce', ['to-number', ['get', 'DISTVERTLOWER_VAL']], 0], 0],
          FT_TO_M,
        ],
        'fill-extrusion-height': [
          '*',
          ['max',
            ['coalesce', ['to-number', ['get', 'DISTVERTUPPER_VAL']], 10_000],
            // Ensure height > base so MapLibre actually renders the prism.
            ['+', ['coalesce', ['to-number', ['get', 'DISTVERTLOWER_VAL']], 0], 100],
          ],
          FT_TO_M,
        ],
      },
    });
  }

  // TFRs — no altitude in current proxy, so fall back to surface→18k.
  // When the TFR proxy is upgraded to expose altitude blocks, the same paint
  // expression will start using real floors/ceilings transparently.
  if (!map.getLayer('tfr-3d') && map.getSource('tfr')) {
    map.addLayer({
      id: 'tfr-3d',
      type: 'fill-extrusion',
      source: 'tfr',
      layout: { visibility: 'none' },
      paint: {
        'fill-extrusion-color': '#dc2626',
        'fill-extrusion-opacity': 0.25,
        'fill-extrusion-base': [
          '*',
          ['max',
            ['coalesce', ['to-number', ['get', 'FLOOR_FT']], TFR_DEFAULT_FLOOR_FT],
            0,
          ],
          FT_TO_M,
        ],
        'fill-extrusion-height': [
          '*',
          ['coalesce', ['to-number', ['get', 'CEILING_FT']], TFR_DEFAULT_CEILING_FT],
          FT_TO_M,
        ],
      },
    });
  }

  // Special Use Airspace — MOAs, R-areas, etc. UPPER_VAL/LOWER_VAL are
  // strings in the SUA proxy; coerce via to-number.
  if (!map.getLayer('sua-3d') && map.getSource('sua')) {
    map.addLayer({
      id: 'sua-3d',
      type: 'fill-extrusion',
      source: 'sua',
      layout: { visibility: 'none' },
      paint: {
        'fill-extrusion-color': [
          'match', ['get', 'TYPE_CODE'],
          'MOA', '#9333ea',           // purple
          'R', '#b91c1c',             // restricted - red
          'P', '#b91c1c',             // prohibited - red
          'W', '#b91c1c',             // warning - red
          'A', '#d97706',             // alert - amber
          /* default */ '#6b7280',
        ],
        'fill-extrusion-opacity': 0.20,
        'fill-extrusion-base': [
          '*',
          ['max',
            ['coalesce', ['to-number', ['get', 'LOWER_VAL']], 0],
            0,
          ],
          FT_TO_M,
        ],
        'fill-extrusion-height': [
          '*',
          ['coalesce', ['to-number', ['get', 'UPPER_VAL']], 18_000],
          FT_TO_M,
        ],
      },
    });
  }
}

/**
 * Show or hide all 3D extrusion layers in one call. The 2D fills/lines for
 * classBCD / tfr / sua are unchanged — caller controls them separately.
 */
export function setExtrusionVisible(map: MapLibreMap, visible: boolean): void {
  const v = visible ? 'visible' : 'none';
  for (const id of EXTRUSION_LAYER_IDS) {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', v);
  }
}

/**
 * Apply a feature filter to all extrusion layers. Useful for the UI toggles
 * that restrict the underlying 2D fills (e.g. "only show TFRs that affect
 * my filed route altitude"). Pass `null` to clear.
 */
export function setExtrusionFilter(map: MapLibreMap, filter: FilterSpecification | null): void {
  for (const id of EXTRUSION_LAYER_IDS) {
    if (map.getLayer(id)) {
      if (filter) map.setFilter(id, filter);
      else map.setFilter(id, null);
    }
  }
}

/**
 * Strip every layer this module added. Use on feature-flag-off or unmount.
 * Sources (classBCD / tfr / sua) are NOT removed because the 2D layers still
 * need them.
 */
export function removeAirspaceExtrusion(map: MapLibreMap): void {
  for (const id of EXTRUSION_LAYER_IDS) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
}
